/* cspell:disable */

import { isExtension } from '../../../../browser-extension/utils/extension-utils';
import { cleanHtmlContent, getClonedHTMLWithInlinedIframes } from './htmlUtils';

// Function to generate a hash of the page context (URL + content)
async function hashPageContext(content: string): Promise<string> {
  const combined = content;
  const encoder = new TextEncoder();
  const data = encoder.encode(combined);

  // Use Web Crypto API for hashing
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  // Fallback for environments without crypto.subtle (shouldn't happen in modern browsers)
  // Simple hash function as fallback
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

// Function to get the current page content
export const getCurrentPageContent = async (): Promise<string> => {
  if (isExtension) {
    try {
      const response = await chrome.runtime.sendMessage({
        type: 'GET_CURRENT_PAGE_CONTENT',
      });

      // If response contains page content in data field, use it; otherwise log a warning and fallback
      if (response && response.success && response.data) {
        return `<pageurl>${response.data.url}</pageurl><pagetitle>${response.data.title}</pagetitle><pagecontent>${JSON.stringify(response.data.content)}</pagecontent>`;
      } else {
        console.warn(
          '[contextUtils] getCurrentPageContent: Response missing success or data:',
          response,
        );
      }
    } catch (error) {
      console.error(
        '[contextUtils] getCurrentPageContent: Error getting page content from extension:',
        error,
      );
    }
  } else {
    let pageUrl = '';
    if (typeof window !== 'undefined') {
      pageUrl = `<pageurl>${window.location.href}</pageurl>`;
    }
    const pageContent = `<pagecontent>${JSON.stringify(
      cleanHtmlContent(getClonedHTMLWithInlinedIframes()).slice(0, 50_000),
    )}</pagecontent>`;
    return `${pageUrl}${pageContent}`;
  }
  return '';
};

// Function to prepare the message with context
// Returns both the formatted message and the new hash (if context was included)
export const prepareMessageWithContext = async (
  userMessage: string,
  isUrlEnabled: boolean,
  isContentEnabled: boolean,
  lastPageContextHash?: string | null,
): Promise<{ formattedMessage: string; newPageContextHash: string | null }> => {
  // Start with the user's message
  let formattedMessage = `<usermessage>${userMessage.trim()}</usermessage>`;
  let newPageContextHash: string | null = lastPageContextHash || null;

  // Add context section if any context is enabled
  if (isUrlEnabled || isContentEnabled) {
    const pageContent = await getCurrentPageContent();

    // Compute hash of current page context
    const currentHash = await hashPageContext(pageContent);

    // Only include context if hash has changed (or if this is the first time)
    const shouldIncludeContext =
      !lastPageContextHash || currentHash !== lastPageContextHash;

    if (shouldIncludeContext) {
      formattedMessage +=
        "<contextinstruction>Here's some additional context that you should only use if the user message is related to it</contextinstruction>\n";
      formattedMessage += '<context>';
      if (isContentEnabled) {
        formattedMessage += '  ' + pageContent + '\n';
      }
      formattedMessage += '</context>';
      newPageContextHash = currentHash;
    } else {
      // Hash hasn't changed, don't include context but keep the same hash
      newPageContextHash = lastPageContextHash;
    }
  }

  return { formattedMessage, newPageContextHash };
};
